import type { AxiosResponse } from 'axios';
import axiosInstance from '../axiosInstance';

export abstract class BaseService<
	T,
	TIDKey extends string,
	TArrayKey extends string,
> {
	protected crudResponseObjectName: string;
	protected crudResponseArrayName: string;
	protected objectID: TIDKey;

	constructor(
		objectID: TIDKey,
		crudResponseObjectName: string,
		crudResponseArrayName: string,
	) {
		this.crudResponseObjectName = crudResponseObjectName;
		this.crudResponseArrayName = crudResponseArrayName;
		this.objectID = objectID;
	}

	async getAllResources(queryParams?: {
		[key: string]: string | undefined;
	}): Promise<TavoittajaApiArrayResponse<TArrayKey, T[]>> {
		const response: AxiosResponse<
			TavoittajaApiArrayResponse<TArrayKey, T[]>
		> = await axiosInstance.get(`/app/${this.crudResponseArrayName}`, {
			params: queryParams,
		});

		return response.data;
	}

	async getSingleResource(
		resourceID: number,
		queryParams?: { [key: string]: string },
	): Promise<T> {
		const response: AxiosResponse<TavoittajaApiResponse<T>> =
			await axiosInstance.get(
				`/app/${this.crudResponseArrayName}/${resourceID}`,
				{
					params: {
						...queryParams,
					},
				},
			);

		const responseObject = (response.data.data as Record<string, T>)?.[
			this.crudResponseObjectName
		];

		if (!responseObject) {
			throw new Error('Response did not contain valid data.');
		}

		return responseObject;
	}

	async deleteSingleResource(resourceID: number): Promise<void> {
		const data = {
			[`${this.crudResponseObjectName}ID`]: resourceID,
		};
		await axiosInstance.delete(`/app/${this.crudResponseArrayName}`, {
			data,
		});
	}

	async create<TRequest, TResponse>(
		data: Partial<TRequest>,
	): Promise<TResponse> {
		const response: AxiosResponse<TavoittajaApiResponse<TResponse>> =
			await axiosInstance.post(
				`/app/${this.crudResponseArrayName}`,
				data,
			);
		console.log(response);

		const responseObject = (
			response.data.data as Record<string, TResponse>
		)?.[this.crudResponseObjectName];

		if (!responseObject) {
			throw new Error('Response did not contain valid data.');
		}

		return responseObject;
	}

	async update<TRequest, TResponse>(
		data: Partial<TRequest>,
	): Promise<TResponse> {
		const response: AxiosResponse<TavoittajaApiResponse<TResponse>> =
			await axiosInstance.patch(
				`/app/${this.crudResponseArrayName}`,
				data,
			);

		const responseObject = (
			response.data.data as Record<string, TResponse>
		)?.[this.crudResponseObjectName];

		if (!responseObject) {
			throw new Error('Response did not contain valid data.');
		}

		return responseObject;
	}

	async deleteMultipleResources(data: {
		[key: string]: ({
			[K in TIDKey]: number;
		} & { delete: boolean })[];
	}): Promise<void> {
		await axiosInstance.post(
			`/app/${this.crudResponseArrayName}/batch`,
			data,
		);
	}

	async createMultiple<TData>(data: {
		[key: string]: Partial<T>[];
	}): Promise<TavoittajaApiResponse<TData>> {
		const response: AxiosResponse<TavoittajaApiResponse<TData>> =
			await axiosInstance.post(
				`/app/${this.crudResponseArrayName}/batch`,
				data,
			);

		return response.data;
	}

	async batch(data: Record<string, Partial<T>[]>): Promise<unknown> {
		const response = await axiosInstance.post(
			`/app/${this.crudResponseArrayName}/batch`,
			data,
		);

		return response.data;
	}

	async getMetaData(language: string) {
		const response: AxiosResponse<
			TavoittajaApiResponse<{ meta: MetaData }>
		> = await axiosInstance.get(
			`/app/${this.crudResponseArrayName}/meta?language=${language}`,
		);
		return response.data;
	}
}
